import { useState, useMemo, useEffect, useContext } from 'react';
import type {
  Report,
  ReportArtist,
  SceneSection,
  GenreMutation,
  InfrastructureEntry,
} from '@tastymusic/research-agent';
import { useArtistData, ArtistDataContext } from '../context/ArtistDataContext';
import { PlatformLinks, type AnyLinks } from './PlatformLinks';
import { ArtistCard } from './ArtistCard';
import { GenrePanel } from './GenrePanel';
import { ClickableArtistName } from './ClickableArtistName';
import './ReportView.css';

// Tab configuration
type TabId = 'discover' | 'genre' | 'scenes' | 'artists' | 'mutations' | 'infrastructure' | 'coda' | 'reference';

const ALL_TABS: { id: TabId; label: string }[] = [
  { id: 'discover', label: 'Discover' },
  { id: 'genre', label: 'Genre' },
  { id: 'scenes', label: 'Scenes' },
  { id: 'artists', label: 'More Artists' },
  { id: 'mutations', label: 'Mutations' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'coda', label: 'Coda' },
  { id: 'reference', label: 'Reference' },
];

// Type for discovered artists passed from ResearchResults
interface DiscoveredArtist {
  name: string;
  genres: string[];
  era: string | null;
  location: string | null;
  matchReason: string;
  confidence: number;
  tracks: string[];
  links?: {
    bandcamp?: string;
    spotify?: string;
    youtube?: string;
  };
  youtubeVideoId?: string;
  bandcampEmbed?: {
    embedHtml: string;
    albumId?: string;
    title?: string;
  };
}

// Quick Reference Row - uses hook to fetch enriched artist data
interface QuickRefEntry {
  artistName: string;
  genreStyle?: string;
  location?: string | null;
  bandcamp?: string;
  spotify?: string;
  apple?: string;
}

function QuickRefRow({ entry }: { entry: QuickRefEntry }) {
  const { data: artistData } = useArtistData(entry.artistName);

  const links: AnyLinks = {
    ...(artistData?.links || {}),
    bandcamp: artistData?.links?.bandcamp || entry.bandcamp,
    spotify: artistData?.links?.spotify || entry.spotify,
    apple: artistData?.links?.apple || entry.apple,
  };

  return (
    <div className="table-row">
      <span className="artist-name">
        <ClickableArtistName name={entry.artistName} />
      </span>
      <span className="genre-style">{entry.genreStyle}</span>
      <span className="location">{entry.location || '—'}</span>
      <span className="links">
        <PlatformLinks
          links={links}
          artistName={entry.artistName}
          size="small"
          showSearchFallbacks={true}
        />
      </span>
    </div>
  );
}

// Navigation Chips Component
interface NavChipsProps {
  tabs: TabId[];
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  buildingTab?: TabId;
  position: 'top' | 'bottom';
}

function NavChips({ tabs, activeTab, onTabChange, buildingTab, position }: NavChipsProps) {
  const handleClick = (tab: TabId) => {
    if (position === 'bottom') {
      // For bottom chips: change tab first, then scroll after render completes
      onTabChange(tab);
      // Use double rAF to ensure scroll happens after React render + paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    } else {
      onTabChange(tab);
    }
  };

  return (
    <div className={`report-nav-chips ${position}`}>
      {ALL_TABS.map(tab => {
        const isAvailable = tabs.includes(tab.id);
        const isActive = activeTab === tab.id;
        const isBuilding = buildingTab === tab.id;

        return (
          <button
            key={tab.id}
            className={`nav-chip ${isActive ? 'active' : ''} ${!isAvailable ? 'disabled' : ''} ${isBuilding ? 'building' : ''}`}
            onClick={() => isAvailable && handleClick(tab.id)}
            disabled={!isAvailable}
          >
            {tab.label}
            {isBuilding && <span className="building-indicator" />}
          </button>
        );
      })}
    </div>
  );
}

// Genre context for Discover tab
interface GenreContext {
  description: string;
  keyArtists: {
    name: string;
    track: string;
    links?: {
      bandcamp?: string;
      spotify?: string;
      youtube?: string;
      youtubeVideoId?: string;
    };
  }[];
}

// Default platform links generator
const defaultGetPlatformLinks = (artistName: string) => {
  const encoded = encodeURIComponent(artistName);
  return {
    bandcamp: `https://bandcamp.com/search?q=${encoded}`,
    spotify: `https://open.spotify.com/search/${encoded}`,
    apple: `https://music.apple.com/search?term=${encoded}`,
    tidal: `https://tidal.com/search?q=${encoded}`,
    amazon: `https://music.amazon.com/search/${encoded}`,
  };
};

interface ReportViewProps {
  report: Report;
  youtubeCache: Record<string, string>;
  theme?: 'light' | 'dark';
  extraArtists?: DiscoveredArtist[];
  buildingTab?: TabId;
  // Discover tab props
  genreContext?: GenreContext;
  discoveredArtists?: DiscoveredArtist[];
  getPlatformLinks?: (name: string) => {
    bandcamp: string;
    spotify: string;
    apple: string;
    tidal: string;
    amazon: string;
  };
}

export default function ReportView({
  report,
  theme = 'light',
  extraArtists = [],
  buildingTab,
  genreContext,
  discoveredArtists = [],
  getPlatformLinks,
}: ReportViewProps) {
  // Default to discover tab if we have discovery content, otherwise genre
  const defaultTab: TabId = genreContext || discoveredArtists.length > 0 ? 'discover' : 'genre';
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);

  // Track visited tabs - once a tab is visited, it stays rendered (but hidden) to preserve iframe state
  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(new Set([defaultTab]));

  // Update visited tabs when active tab changes
  useEffect(() => {
    if (!visitedTabs.has(activeTab)) {
      setVisitedTabs(prev => new Set([...prev, activeTab]));
    }
  }, [activeTab, visitedTabs]);

  // Prefetch all artist data immediately when report loads
  const artistDataContext = useContext(ArtistDataContext);
  useEffect(() => {
    if (!report || !artistDataContext) return;

    // Extract all artist names + tracks from report
    const artistsToFetch: Array<{ name: string; tracks: string[] }> = [];
    const seen = new Set<string>();

    const addArtist = (name: string, tracks: string[] = []) => {
      if (!name || seen.has(name)) return;
      seen.add(name);
      artistsToFetch.push({ name, tracks });
    };

    // Helper to extract tracks from ReportArtist
    const getArtistTracks = (a: ReportArtist) => {
      const tracks: string[] = [];
      if (a.featuredTrack?.name) tracks.push(a.featuredTrack.name);
      if (a.otherTracks) tracks.push(...a.otherTracks);
      return tracks;
    };

    // Founders
    report.founders?.founders?.forEach(a => addArtist(a.name, getArtistTracks(a)));
    report.founders?.parentGenres?.forEach(pg => addArtist(pg.keyArtist));

    // Scenes
    report.scenes?.forEach(scene => {
      scene.artists?.forEach(a => addArtist(a.name, getArtistTracks(a)));
    });

    // Mutations
    report.mutations?.forEach(mutation => {
      mutation.keyArtists?.forEach(a => addArtist(a.name, getArtistTracks(a)));
    });

    // More artists
    report.moreArtists?.artists?.forEach(a => addArtist(a.name, getArtistTracks(a)));

    // Quick reference
    report.quickReference?.forEach(q => addArtist(q.artistName));

    // Also prefetch discovered + extra artists
    discoveredArtists?.forEach(a => addArtist(a.name, a.tracks));
    extraArtists?.forEach(a => addArtist(a.name, a.tracks));

    // Prefetch all artists in parallel (fire and forget)
    if (artistsToFetch.length > 0) {
      console.log(`[ReportView] Prefetching ${artistsToFetch.length} artists...`);
      artistsToFetch.forEach(({ name, tracks }) => {
        artistDataContext.fetchArtistData(name, tracks).catch(() => {});
      });
    }
  }, [report, discoveredArtists, extraArtists, artistDataContext]);

  if (!report) return null;

  // Safely get arrays
  const scenes = Array.isArray(report.scenes) ? report.scenes : [];
  const mutations = Array.isArray(report.mutations) ? report.mutations : [];
  const quickRef = Array.isArray(report.quickReference) ? report.quickReference : [];
  const founderArtists = report.founders?.founders && Array.isArray(report.founders.founders)
    ? report.founders.founders : [];
  const parentGenres = report.founders?.parentGenres && Array.isArray(report.founders.parentGenres)
    ? report.founders.parentGenres : [];
  const moreArtists = report.moreArtists?.artists && Array.isArray(report.moreArtists.artists)
    ? report.moreArtists.artists : [];
  const infraLabels = report.infrastructure?.labels && Array.isArray(report.infrastructure.labels)
    ? report.infrastructure.labels : [];
  const infraRadio = report.infrastructure?.radio && Array.isArray(report.infrastructure.radio)
    ? report.infrastructure.radio : [];
  const infraFestivals = report.infrastructure?.festivals && Array.isArray(report.infrastructure.festivals)
    ? report.infrastructure.festivals : [];

  // Determine available tabs based on content
  const availableTabs = useMemo(() => {
    const tabs: TabId[] = [];
    if (genreContext || discoveredArtists.length > 0) tabs.push('discover');
    if (report.introduction?.content || founderArtists.length > 0 || parentGenres.length > 0) tabs.push('genre');
    if (scenes.length > 0) tabs.push('scenes');
    if (moreArtists.length > 0 || extraArtists.length > 0) tabs.push('artists');
    if (mutations.length > 0) tabs.push('mutations');
    if (infraLabels.length > 0 || infraRadio.length > 0 || infraFestivals.length > 0) tabs.push('infrastructure');
    if (report.coda?.content) tabs.push('coda');
    if (quickRef.length > 0) tabs.push('reference');
    return tabs;
  }, [report, scenes, mutations, quickRef, founderArtists, parentGenres, moreArtists, extraArtists, infraLabels, infraRadio, infraFestivals, genreContext, discoveredArtists]);

  // Auto-select first available tab if current isn't available
  const effectiveTab = availableTabs.includes(activeTab) ? activeTab : (availableTabs[0] || 'genre');

  return (
    <article className="report-view" data-theme={theme}>
      {/* Header */}
      {report.title && (
        <header className="report-header">
          <h1 className="report-title">{report.title}</h1>
          {report.subtitle && <p className="report-subtitle">{report.subtitle}</p>}
          {report.tagline && <p className="report-tagline">{report.tagline}</p>}
        </header>
      )}

      {/* Top navigation chips */}
      <NavChips
        tabs={availableTabs}
        activeTab={effectiveTab}
        onTabChange={setActiveTab}
        buildingTab={buildingTab}
        position="top"
      />

      {/* Discover Tab: Genre Panel + Discovered Artists */}
      {/* NOTE: Tabs only render once visited, then stay mounted (hidden) to preserve iframe state */}
      {visitedTabs.has('discover') && (
      <div className={`tab-content discover-tab ${effectiveTab !== 'discover' ? 'tab-hidden' : ''}`}>
          {genreContext && (
            <GenrePanel
              description={genreContext.description}
              keyArtists={genreContext.keyArtists}
              getPlatformLinks={getPlatformLinks || defaultGetPlatformLinks}
            />
          )}

          {(() => {
            // Filter out artists already shown in genre panel
            const genrePanelNames = new Set(
              (genreContext?.keyArtists || []).map(a => a.name.toLowerCase())
            );
            const filteredArtists = discoveredArtists.filter(
              a => !genrePanelNames.has(a.name.toLowerCase())
            );

            if (filteredArtists.length === 0) return null;

            return (
              <div className="discovered-artists-section">
                <h3 className="discovered-artists-title">
                  Discovered {Math.min(filteredArtists.length, 6)} Artists
                </h3>
                <div className="discovered-artists-grid">
                  {filteredArtists.slice(0, 6).map((artist) => (
                    <ArtistCard
                      key={artist.name}
                      artist={artist}
                      getPlatformLinks={getPlatformLinks || defaultGetPlatformLinks}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Genre Tab: Introduction + Founders */}
      {visitedTabs.has('genre') && (
      <div className={`tab-content ${effectiveTab !== 'genre' ? 'tab-hidden' : ''}`}>
          {/* Introduction */}
          {report.introduction?.content && (
            <section className="report-section report-introduction">
              <h2>{report.introduction.sectionTitle || 'Introduction'}</h2>
              <div className="section-content">
                {report.introduction.content.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </section>
          )}

          {/* Founders & Lineage */}
          {(founderArtists.length > 0 || parentGenres.length > 0) && (
            <section className="report-section report-founders">
              <h2>{report.founders?.title || 'The Originators'}</h2>

              {parentGenres.length > 0 && (
                <div className="parent-genres">
                  <h3>The Roots</h3>
                  <div className="genre-cards">
                    {parentGenres.length >= 2 ? (
                      <>
                        <div className="genre-card">
                          <h4>{parentGenres[0].name}</h4>
                          <p>{parentGenres[0].description}</p>
                          {parentGenres[0].keyArtist && <span className="key-artist">Key Artist: <ClickableArtistName name={parentGenres[0].keyArtist} /></span>}
                        </div>
                        <div className="genre-connection">
                          <svg viewBox="0 0 40 80" className="connection-arrows">
                            <path d="M 5,20 Q 20,40 5,60" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M 35,20 Q 20,40 35,60" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                        </div>
                        <div className="genre-card">
                          <h4>{parentGenres[1].name}</h4>
                          <p>{parentGenres[1].description}</p>
                          {parentGenres[1].keyArtist && <span className="key-artist">Key Artist: <ClickableArtistName name={parentGenres[1].keyArtist} /></span>}
                        </div>
                      </>
                    ) : (
                      parentGenres.map((genre, i) => (
                        <div key={i} className="genre-card">
                          <h4>{genre.name}</h4>
                          <p>{genre.description}</p>
                          {genre.keyArtist && <span className="key-artist">Key Artist: <ClickableArtistName name={genre.keyArtist} /></span>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {report.founders?.lineageDescription && (
                <div className="section-content">
                  <p>{report.founders.lineageDescription}</p>
                </div>
              )}

              {founderArtists.length > 0 && (
                <div className="artists-grid">
                  {founderArtists.map((artist, i) => (
                    <ArtistSection key={i} artist={artist} getPlatformLinks={getPlatformLinks || defaultGetPlatformLinks} />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* Scenes Tab */}
      {visitedTabs.has('scenes') && (
      <div className={`tab-content ${effectiveTab !== 'scenes' ? 'tab-hidden' : ''}`}>
          {scenes.map((scene, idx) => (
            <SceneSectionView key={scene.id || idx} scene={scene} getPlatformLinks={getPlatformLinks || defaultGetPlatformLinks} />
          ))}
        </div>
      )}

      {/* More Artists Tab */}
      {visitedTabs.has('artists') && (
      <div className={`tab-content ${effectiveTab !== 'artists' ? 'tab-hidden' : ''}`}>
          <section className="report-section report-more-artists">
            <h2>{report.moreArtists?.sectionTitle || 'More Artists'}</h2>
            {report.moreArtists?.description && <p className="section-intro">{report.moreArtists.description}</p>}

            {/* Combined artists grid: report artists + discovery artists in rows of 3 */}
            <div className="artists-combined-grid">
              {/* Report's More Artists */}
              {moreArtists.map((artist, i) => (
                <ArtistSection key={`report-${i}`} artist={artist} getPlatformLinks={getPlatformLinks || defaultGetPlatformLinks} />
              ))}

              {/* Discovery artists with ArtistCard */}
              {extraArtists.length > 0 && getPlatformLinks && (
                <>
                  {moreArtists.length > 0 && (
                    <div className="artists-section-divider">
                      <span>More to Explore</span>
                    </div>
                  )}
                  <div className="extra-artists-grid">
                    {extraArtists.map((artist) => (
                      <ArtistCard
                        key={artist.name}
                        artist={artist}
                        getPlatformLinks={getPlatformLinks}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      )}

      {/* Mutations Tab */}
      {visitedTabs.has('mutations') && (
      <div className={`tab-content ${effectiveTab !== 'mutations' ? 'tab-hidden' : ''}`}>
          <section className="report-section report-mutations">
            <h2>Genre Mutations</h2>
            {mutations.map((mutation, idx) => (
              <MutationSection key={mutation.id || idx} mutation={mutation} getPlatformLinks={getPlatformLinks || defaultGetPlatformLinks} />
            ))}
          </section>
        </div>
      )}

      {/* Infrastructure Tab */}
      {visitedTabs.has('infrastructure') && (
      <div className={`tab-content ${effectiveTab !== 'infrastructure' ? 'tab-hidden' : ''}`}>
          <section className="report-section report-infrastructure">
            <h2>{report.infrastructure?.sectionTitle || 'The DIY Infrastructure'}</h2>
            {report.infrastructure?.description && <p className="section-intro">{report.infrastructure.description}</p>}

            {infraLabels.length > 0 && (
              <div className="infra-category">
                <h3>Labels That Matter</h3>
                {infraLabels.map((entry, i) => (
                  <InfraEntry key={i} entry={entry} />
                ))}
              </div>
            )}

            {infraRadio.length > 0 && (
              <div className="infra-category">
                <h3>Radio & Discovery</h3>
                {infraRadio.map((entry, i) => (
                  <InfraEntry key={i} entry={entry} />
                ))}
              </div>
            )}

            {infraFestivals.length > 0 && (
              <div className="infra-category">
                <h3>Festivals</h3>
                {infraFestivals.map((entry, i) => (
                  <InfraEntry key={i} entry={entry} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Coda Tab */}
      {visitedTabs.has('coda') && (
      <div className={`tab-content ${effectiveTab !== 'coda' ? 'tab-hidden' : ''}`}>
          <section className="report-section report-coda">
            <h2>{report.coda?.sectionTitle || 'Why This Matters'}</h2>
            <div className="section-content coda-content">
              {report.coda?.content?.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Reference Tab */}
      {visitedTabs.has('reference') && (
      <div className={`tab-content ${effectiveTab !== 'reference' ? 'tab-hidden' : ''}`}>
          <section className="report-section report-quick-ref">
            <div className="quick-ref-table">
              <div className="table-header">
                <span>Artist</span>
                <span>Genre/Style</span>
                <span>Location</span>
                <span>Links</span>
              </div>
              {quickRef.map((entry, i) => (
                <QuickRefRow key={i} entry={entry} />
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Bottom navigation chips */}
      <NavChips
        tabs={availableTabs}
        activeTab={effectiveTab}
        onTabChange={setActiveTab}
        buildingTab={buildingTab}
        position="bottom"
      />

      </article>
  );
}

// Convert ReportArtist to ArtistCard-compatible format
function reportArtistToCardArtist(artist: ReportArtist) {
  // Combine featured track + other tracks for key tracks display
  const tracks: string[] = [];
  if (artist.featuredTrack?.name) tracks.push(artist.featuredTrack.name);
  if (Array.isArray(artist.otherTracks)) tracks.push(...artist.otherTracks);

  return {
    name: artist.name,
    genres: [],
    era: null,
    location: artist.location,
    matchReason: '', // Not shown for report artists
    confidence: 1,
    tracks,
    links: artist.links,
    youtubeVideoId: artist.verifiedYoutubeId,
    bandcampEmbed: artist.verifiedBandcampEmbed ? {
      embedHtml: artist.verifiedBandcampEmbed.embedHtml,
      albumId: artist.verifiedBandcampEmbed.albumId,
      title: artist.verifiedBandcampEmbed.title,
      bandcampUrl: artist.verifiedBandcampEmbed.bandcampUrl,
    } : undefined,
  };
}

// Artist Section Component - uses ArtistCard for everything (cached, versioned)
function ArtistSection({ artist, getPlatformLinks }: { artist: ReportArtist; getPlatformLinks: (name: string) => { bandcamp: string; spotify: string; apple: string; tidal: string; amazon: string } }) {
  if (!artist || !artist.name) return null;

  // Convert to ArtistCard format - includes all tracks for embed resolution
  const cardArtist = reportArtistToCardArtist(artist);

  return (
    <div className="artist-section">
      {/* Report-specific narrative content */}
      {(artist.story || artist.whyTheyMatter) && (
        <div className="artist-content">
          {artist.story && (
            <div className="artist-story">
              {artist.story.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}

          {artist.whyTheyMatter && (
            <div className="artist-why-matters">
              <strong>Why They Matter:</strong> {artist.whyTheyMatter}
            </div>
          )}
        </div>
      )}

      {/* ArtistCard handles: embed, members, key tracks, platform links, location */}
      <ArtistCard
        artist={cardArtist}
        getPlatformLinks={getPlatformLinks}
        variant="full"
      />

      {/* Featured track description if provided */}
      {artist.featuredTrack?.description && (
        <p className="track-description">{artist.featuredTrack.description}</p>
      )}
    </div>
  );
}

// Scene Section Component
function SceneSectionView({ scene, getPlatformLinks }: { scene: SceneSection; getPlatformLinks: (name: string) => { bandcamp: string; spotify: string; apple: string; tidal: string; amazon: string } }) {
  if (!scene) return null;

  const artists = Array.isArray(scene.artists) ? scene.artists : [];
  const keyVenues = Array.isArray(scene.keyVenues) ? scene.keyVenues : [];
  const keyLabels = Array.isArray(scene.keyLabels) ? scene.keyLabels : [];

  return (
    <section className="report-section report-scene">
      <h2>{scene.title || 'Scene'}</h2>
      {scene.location && (
        <div className="scene-meta">
          <span className="scene-location">{scene.location}</span>
        </div>
      )}

      {scene.description && (
        <div className="section-content">
          {scene.description.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}

      {scene.whyHere && (
        <div className="why-here">
          <strong>Why Here:</strong> {scene.whyHere}
        </div>
      )}

      {keyVenues.length > 0 && (
        <div className="scene-venues">
          <span className="label">Key Venues:</span>
          {keyVenues.map((venue, i) => (
            <span key={i} className="venue">{venue}</span>
          ))}
        </div>
      )}

      {keyLabels.length > 0 && (
        <div className="scene-labels">
          <span className="label">Key Labels:</span>
          {keyLabels.map((label, i) => (
            <span key={i} className="label-name">{label}</span>
          ))}
        </div>
      )}

      {artists.length > 0 && (
        <div className="artists-grid">
          {artists.map((artist, i) => (
            <ArtistSection key={i} artist={artist} getPlatformLinks={getPlatformLinks} />
          ))}
        </div>
      )}
    </section>
  );
}

// Mutation Section Component
function MutationSection({ mutation, getPlatformLinks }: { mutation: GenreMutation; getPlatformLinks: (name: string) => { bandcamp: string; spotify: string; apple: string; tidal: string; amazon: string } }) {
  if (!mutation) return null;

  const keyArtists = Array.isArray(mutation.keyArtists) ? mutation.keyArtists : [];

  return (
    <div className="mutation-section">
      <h3 className="mutation-name">{mutation.name}</h3>
      {mutation.description && <p className="mutation-description">{mutation.description}</p>}
      {mutation.soundDescription && (
        <p className="mutation-sound">
          <strong>The Sound:</strong> {mutation.soundDescription}
        </p>
      )}

      {keyArtists.length > 0 && (
        <div className="mutation-artists">
          {keyArtists.map((artist, i) => (
            <ArtistSection key={i} artist={artist} getPlatformLinks={getPlatformLinks} />
          ))}
        </div>
      )}
    </div>
  );
}

// Infrastructure Entry Component
function InfraEntry({ entry }: { entry: InfrastructureEntry | string }) {
  if (typeof entry === 'string') {
    return <div className="infra-entry"><span className="infra-name">{entry}</span></div>;
  }

  if (!entry) return null;

  return (
    <div className="infra-entry">
      <div className="infra-header">
        <h4 className="infra-name">{entry.name}</h4>
        {entry.location && <span className="infra-location">{entry.location}</span>}
      </div>
      {entry.description && <p className="infra-description">{entry.description}</p>}
      {entry.whyItMatters && (
        <p className="infra-why">
          <strong>Why It Matters:</strong> {entry.whyItMatters}
        </p>
      )}
      {entry.links?.website && (
        <a href={entry.links.website} target="_blank" rel="noopener noreferrer" className="infra-link">
          Website
        </a>
      )}
      {entry.links?.bandcamp && (
        <a href={entry.links.bandcamp} target="_blank" rel="noopener noreferrer" className="infra-link bandcamp">
          Bandcamp
        </a>
      )}
    </div>
  );
}

// Export TabId type for ResearchResults to use
export type { TabId };
